<script lang="ts" setup>
import countTo from 'vue-count-to'
import { ref } from 'vue'
import type { SubscribePageConfig } from '@/composables/payment'
import {
  PaymentMethodSelector,
  createShowPaymentMethodSelector,
  useSubscription,
} from '@/composables/payment'
import { VIP_TYPE } from '@/constants'
import { brand } from '@/config/brands'

const props = defineProps<{
  config: SubscribePageConfig
}>()

// 使用订阅 composable（支持多支付方式）
const {
  subscribeProducts,
  getUnitPrice,
  onSubscribeProducts,
  getIsImageBackground,
  isZero,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = useSubscription('store')

// 是否显示支付方式选择器
const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const active = ref(0)
// 定义样式变量供 CSS v-bind 使用
const redGradient = props.config.colors.redGradient
const whiteColor = '#FFFFFF'
const vipBg = `${props.config.resources.vipBgColor} url(${props.config.resources.vipBg}) no-repeat top left`

const subscribeText1 = props.config.colors.subscribeText1
const subscribeText2 = props.config.colors.subscribeText2
const subscribeText3 = props.config.colors.subscribeText3
const subscribeText4 = props.config.colors.subscribeText4
</script>

<template>
  <div class="subscribe-cover h-100dvh">
    <BasicNavbar class="text-white" />

    <div class="px-24px">
      <div class="subscribe-title text-20px font-bold h-44px flex items-center justify-center">
        {{ config.texts.vipMembership }}
      </div>
      <div class="subscribe-text-2 text-14px">
        {{ config.texts.membershipDesc }}
      </div>
      <!-- 未开通提示：蓝湖「VIP」14px 棕金色（#B59C7D） -->
      <div class="subscribe-text-1 text-14px leading-20px mt-4px mb-20px">
        {{ config.texts.notJoinedYet }}
      </div>
    </div>

    <div class="member-wrap px-16px bg-#100f17">
      <div v-for="(item, index) in subscribeProducts" :key="index">
        <div
          v-if="item.isShow"
          class="dc-item subscribe-text-2 relative mb-10px"
          :class="{ 'dc-item--active': active === index }"
          :style="{ color: getIsImageBackground(item) ? subscribeText4 : subscribeText2 }"
          @click="() => { active = index; onSubscribeProducts(item); }"
        >
          <!-- 角标, 两种会员都有 -->
          <div v-if="item.cornerTip && item.marketingCopy" class="popular-tag">
            <span class="popular-tag-text">{{ item.marketingCopy }}</span>
          </div>

          <!-- 折扣会员 -->
          <div v-if="item.subscribeType === VIP_TYPE.DISCOUNT" class="flex items-start justify-between">
            <!-- 周期水印文字（Monthly / Seasonal），右上角 30px 渐变字 -->
            <div class="absolute top-5px right-5px font-bold text-30px leading-[30px] cycleBg">
              {{ item.cycleStr }}
            </div>

            <div class="flex-1 min-w-0 relative">
              <div class="flex items-center">
                <img class="dc-item-icon flex-shrink-0" src="/images/vowpath/subscribe/dc-card-icon.png" alt="">
                <span class="dc-item-coins">{{ item.discountCoins }}</span>
                <span class="dc-item-name">{{ config.texts.diamondCard }}</span>
              </div>
              <div class="dc-item-desc">
                {{ config.texts.dcUpfrontDaily?.(item.discountOnceCoins ?? 0, item.discountDailyCoins ?? 0) }}
              </div>
            </div>
            <div class="flex-shrink-0 flex flex-col items-center relative dc-item-price-col">
              <div class="dc-item-price">
                $<countTo
                  :start-val="Number(item.discountOriginalPrice)"
                  :end-val="Number(item.preferentialPrice || item.price)"
                  :duration="3000"
                  :decimals="2"
                />
              </div>
              <div class="dc-item-strike line-through">
                $ {{ item.discountOriginalPrice }}
              </div>
            </div>
          </div>

          <!-- 畅读会员 -->
          <div v-else class="flex items-center">
            <img v-if="brand !== 'ficmoon'" class="flex-shrink-0 mr-16px" :src="config.resources.vipIcon" width="22">
            <div class="flex-1">
              <div class="text-18px font-medium">
                {{ item.cycleStr }}
              </div>
              <div class="subscribe-text-1 text-12px leading-16px">
                {{ item.subscribeText }}
              </div>
            </div>

            <div class="flex-shrink-0 text-right flex flex-col items-end gap-4px">
              <!-- 免费 -->
              <template v-if="isZero(item.preferentialPrice)">
                <div class="price-btn-subscribe text-13px font-medium text-white">
                  {{ config.texts.free }}
                </div>
                <div class="subscribe-text-3 text-12px line-through">
                  $ {{ item.price }}
                </div>
              </template>
              <!-- 无优惠价格 -->
              <template v-else-if="!item.preferentialPrice">
                <div class="price-btn-subscribe text-13px font-medium text-white">
                  $ {{ item.price }}
                </div>
                <div v-if="getUnitPrice(item)" class="subscribe-text-3 text-12px">
                  $ {{ getUnitPrice(item) }}
                </div>
              </template>
              <!-- 有优惠价格 -->
              <template v-else>
                <div class="price-btn-subscribe text-13px font-medium text-white">
                  $ <countTo
                    :start-val="Number(item.price)"
                    :end-val="Number(item.preferentialPrice)"
                    :duration="3000"
                    :decimals="2"
                  />
                </div>
                <div class="subscribe-text-3 text-12px line-through">
                  $ {{ item.price }}
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="px-4px text-10px text-n-text-3 pb-24px">
        <p class="text-12px mt-6px">
          {{ config.texts.tips }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip1 }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip2 }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip3 }}
        </p>
        <p class="mt-6px">
          {{ config.texts.tip4 }}
        </p>
      </div>

      <!-- 支付方式选择器固定在底部 -->
      <div v-if="showPaymentMethodSelector" class="fixed bottom-0px left-0 right-0 flex justify-center">
        <PaymentMethodSelector
          variant="large"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="selectPaymentMethod"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.member-wrap {
  min-height: calc(100vh - 192px);
}

.subscribe-cover {
  width: 100%;
  background: v-bind(vipBg);
  background-size: 100% auto;
}

/* 钻石卡 item：蓝湖「VIP」蒙版 343x94，圆角 8，2px 渐变描边（background 双层实现） */
.dc-item {
  min-height: 94px;
  padding: 25px 14px 0 8px;
  border: 2px solid transparent;
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(44, 39, 61, 1) 0%, rgba(28, 16, 45, 1) 100%) padding-box,
    linear-gradient(135deg, rgba(73, 67, 93, 1) 0%, rgba(57, 45, 74, 1) 100%) border-box;
  overflow: hidden;
}

/* 选中态：横向深紫渐变底 + 青-粉-橙渐变描边 */
.dc-item--active {
  background:
    linear-gradient(90deg, rgba(40, 31, 65, 1) 0%, rgba(28, 16, 45, 1) 100%) padding-box,
    linear-gradient(135deg, rgba(170, 237, 255, 1) 0%, rgba(255, 124, 204, 1) 51%, rgba(255, 198, 155, 1) 100%) border-box;
}

.dc-item-icon {
  width: 35px;
  height: 23px;
  margin-right: 8px;
}

.dc-item-coins {
  font-weight: bold;
  font-size: 18px;
  line-height: 23px;
  color: #FFFFFF;
}

.dc-item-name {
  margin-left: 6px;
  font-weight: bold;
  font-size: 14px;
  line-height: 23px;
  color: #C86BF8;
}

.dc-item-desc {
  margin-top: 8px;
  padding-left: 2px;
  font-size: 13px;
  line-height: 16px;
  color: #9590A1;
}

.dc-item-price-col {
  margin-top: 3px;
}

.dc-item-price {
  font-weight: bold;
  font-size: 20px;
  line-height: 24px;
  color: #FFD483;
}

.dc-item-strike {
  font-size: 12px;
  line-height: 16px;
  color: #B59C7D;
}

/* Popular 角标：51x18，左上贴描边内侧，圆角 6/0/10/0，渐变字 */
.popular-tag {
  position: absolute;
  top: 0;
  left: 0;
  height: 18px;
  padding: 0 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(71, 45, 119, 1) 0%, rgba(100, 56, 103, 1) 100%);
  border-radius: 6px 0px 10px 0px;
  font-weight: 500;
  font-size: 10px;
  line-height: 12px;
}
.popular-tag-text {
  background: linear-gradient(90deg, rgba(204, 66, 247, 1) 0%, rgba(239, 106, 208, 1) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}
.cycleBg {
  background: linear-gradient(0deg, rgba(81, 56, 109, 1) 0%, rgba(36, 25, 54, 1) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  opacity: 0.63;
}

.subscribe-title {
  background: linear-gradient(90deg, #9C5EF9 1%, #F86BCD 34%, #FDB547 65%, #FFE2A4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subscribe-text-1 {
  color: #B59C7D;
}

.price-btn-subscribe {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  padding: 0 18px;
  border-radius: 14px;
  background: url('/images/vowpath/subscribe-btn.png') no-repeat center / 100% 100%;
}

.price-btn {
  display: inline-flex;
  align-items: center;
  padding: 7px 18px;
  border-radius: 14px;
  background: linear-gradient(45deg, rgba(100, 23, 255, 1) 0%, rgba(241, 80, 255, 1) 38%, rgba(241, 80, 255, 1) 70%, rgba(255, 236, 168, 1) 100%);
}

.subscribe-text-2 {
  color: v-bind(subscribeText2);
}
.subscribe-text-3 {
  color: v-bind(subscribeText3);
}
</style>
